import { MathUtils, Vector3 } from 'three';
import type { PerspectiveCamera } from 'three';
import { localToWorld, type VehicleState } from '../model/vehicleDriving';
import {
  sampleDrivingTerrain,
  type DrivingTerrainSample,
} from '../model/drivingTerrain';
import type { DrivingCameraMode } from '../data/drivingCameraOptions';
import type { CarBounds, Vector3Tuple } from '../types';

/**
 * 주행 카메라.
 *
 * 추격 시점은 느슨하게 따라가 속도감을 만들고, 차체에 장착된 세 시점(보닛·범퍼·운전석)은
 * 차량과 거의 강체로 붙어 움직입니다. 네 모드 모두 지형 높이를 샘플링해 경사에서 노면을
 * 파고들지 않습니다.
 *
 * 카메라 소유권은 CarDriver 한 곳에 남겨 둡니다(OrbitControls는 주행 중 비활성).
 */

const CHASE_POSITION_DAMPING = 4.5;
const CHASE_LOOK_DAMPING = 8;
/** 장착 시점은 차체의 일부처럼 움직여야 하므로 훨씬 단단하게 따라갑니다. */
const MOUNTED_POSITION_DAMPING = 26;
const MOUNTED_LOOK_DAMPING = 20;
const FOV_DAMPING = 5;
const MIN_TERRAIN_CLEARANCE = 1;
/** 장착 시점은 노면 위 이 높이까지만 내려갑니다. 범퍼 시점이 아스팔트에 잠기지 않습니다. */
const MIN_MOUNTED_CLEARANCE = 0.2;

const vehicleTerrain: DrivingTerrainSample = { height: 0, slopeX: 0, slopeZ: 0 };
const cameraTerrain: DrivingTerrainSample = { height: 0, slopeX: 0, slopeZ: 0 };
const lookTerrain: DrivingTerrainSample = { height: 0, slopeX: 0, slopeZ: 0 };

export interface DrivingCameraPose {
  mode: DrivingCameraMode;
  /** true면 차체 장착 시점입니다. */
  mounted: boolean;
  /** 카메라 자리 (차량 로컬 X: 양수가 앞, Z: 양수가 오른쪽). */
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  /** 시선이 향할 자리 (차량 로컬 X). */
  lookAheadX: number;
  lookHeight: number;
  fov: number;
}

/**
 * 차량 실측 치수에서 모드별 카메라 자리를 만듭니다.
 *
 * 고정값을 쓰면 전장 4.4m 로드스터와 지붕 1.65m SUV에서 같은 시점이 성립하지 않습니다.
 * 운전석 시점은 매니페스트의 실측 눈 위치(interiorEye)를 그대로 씁니다. 그 값이 없는
 * 외관 전용 모델은 추격으로 정상화합니다(호출 쪽에서 선택 UI도 함께 처리합니다).
 */
export function createDrivingCameraPose(
  mode: DrivingCameraMode,
  bounds: CarBounds,
  fov: number,
  interiorEye?: Vector3Tuple
): DrivingCameraPose {
  const length = bounds.frontX - bounds.rearX;

  if (mode === 'cockpit' && interiorEye !== undefined) {
    return {
      mode,
      mounted: true,
      offsetX: interiorEye[0],
      offsetY: interiorEye[1],
      offsetZ: interiorEye[2],
      lookAheadX: bounds.frontX + length * 0.6,
      lookHeight: interiorEye[1] * 0.82,
      fov,
    };
  }

  if (mode === 'hood') {
    return {
      mode,
      mounted: true,
      // 앞바퀴 축 조금 앞, 보닛 높이. 차체 앞머리가 화면 아래에 걸립니다.
      offsetX: bounds.frontWheelX + length * 0.12,
      offsetY: bounds.roofY * 0.66,
      offsetZ: 0,
      lookAheadX: bounds.frontX + length * 0.7,
      lookHeight: bounds.roofY * 0.5,
      fov,
    };
  }

  if (mode === 'bumper') {
    return {
      mode,
      mounted: true,
      offsetX: bounds.frontX - 0.06,
      offsetY: Math.max(0.34, bounds.tireOuterRadius * 0.92),
      offsetZ: 0,
      lookAheadX: bounds.frontX + length * 0.85,
      lookHeight: Math.max(0.3, bounds.tireOuterRadius * 0.8),
      fov,
    };
  }

  return {
    mode: 'chase',
    mounted: false,
    // 뒤 범퍼에서 전장의 0.85배만큼 더 뒤.
    offsetX: bounds.rearX - length * 0.85,
    offsetY: bounds.roofY * 1.45,
    offsetZ: 0,
    lookAheadX: bounds.frontX + length * 0.25,
    lookHeight: bounds.roofY * 0.55,
    fov,
  };
}

function desiredPosition(state: VehicleState, pose: DrivingCameraPose, into: Vector3): Vector3 {
  const mount = localToWorld(state, pose.offsetX, pose.offsetZ);
  const vehicleY = sampleDrivingTerrain(state.x, state.z, vehicleTerrain).height;
  const terrainAtMount = sampleDrivingTerrain(mount.x, mount.z, cameraTerrain).height;

  if (pose.mounted) {
    /*
     * 장착 시점은 차체 기준 높이를 그대로 따라야 합니다(경사에서 차와 함께 올라갑니다).
     * 지형 높이는 노면 관통만 막는 하한으로 씁니다.
     */
    const y = Math.max(vehicleY + pose.offsetY, terrainAtMount + MIN_MOUNTED_CLEARANCE);
    return into.set(mount.x, y, mount.z);
  }
  const y = Math.max(vehicleY + pose.offsetY, terrainAtMount + MIN_TERRAIN_CLEARANCE);
  return into.set(mount.x, y, mount.z);
}

function desiredLookAt(state: VehicleState, pose: DrivingCameraPose, into: Vector3): Vector3 {
  const ahead = localToWorld(state, pose.lookAheadX, 0);
  const terrainAheadY = sampleDrivingTerrain(ahead.x, ahead.z, lookTerrain).height;
  return into.set(ahead.x, terrainAheadY + pose.lookHeight, ahead.z);
}

const scratch = new Vector3();

/** 차량이 순간이동했거나 시점을 바꿨을 때 곧바로 목표 자리에 놓습니다. */
export function snapDrivingCamera(
  camera: PerspectiveCamera,
  lookAt: Vector3,
  state: VehicleState,
  pose: DrivingCameraPose
): void {
  camera.position.copy(desiredPosition(state, pose, scratch));
  desiredLookAt(state, pose, lookAt);
  if (Math.abs(camera.fov - pose.fov) > 0.001) {
    camera.fov = pose.fov;
    camera.updateProjectionMatrix();
  }
  camera.lookAt(lookAt);
}

/** 한 프레임 갱신. 시점 종류에 따라 감쇠 강도만 달라집니다. */
export function updateDrivingCamera(
  camera: PerspectiveCamera,
  lookAt: Vector3,
  state: VehicleState,
  pose: DrivingCameraPose,
  dt: number
): void {
  const positionDamping = pose.mounted ? MOUNTED_POSITION_DAMPING : CHASE_POSITION_DAMPING;
  const lookDamping = pose.mounted ? MOUNTED_LOOK_DAMPING : CHASE_LOOK_DAMPING;

  const goal = desiredPosition(state, pose, scratch);
  camera.position.x = MathUtils.damp(camera.position.x, goal.x, positionDamping, dt);
  camera.position.y = MathUtils.damp(camera.position.y, goal.y, positionDamping, dt);
  camera.position.z = MathUtils.damp(camera.position.z, goal.z, positionDamping, dt);

  /*
   * x/y/z를 따로 감쇠하면 굽은 사면에서 현재 x/z와 y가 서로 다른 지점을 나타낼 수
   * 있습니다. 감쇠가 끝난 좌표를 다시 샘플링해 카메라가 지형 아래로 내려가지 못하게 합니다.
   */
  const cameraGroundY = sampleDrivingTerrain(
    camera.position.x,
    camera.position.z,
    cameraTerrain
  ).height;
  const clearance = pose.mounted ? MIN_MOUNTED_CLEARANCE : MIN_TERRAIN_CLEARANCE;
  camera.position.y = Math.max(camera.position.y, cameraGroundY + clearance);

  const lookGoal = desiredLookAt(state, pose, scratch);
  lookAt.x = MathUtils.damp(lookAt.x, lookGoal.x, lookDamping, dt);
  lookAt.y = MathUtils.damp(lookAt.y, lookGoal.y, lookDamping, dt);
  lookAt.z = MathUtils.damp(lookAt.z, lookGoal.z, lookDamping, dt);
  const lookGroundY = sampleDrivingTerrain(lookAt.x, lookAt.z, lookTerrain).height;
  lookAt.y = Math.max(lookAt.y, lookGroundY + pose.lookHeight);

  /*
   * 속도가 오르면 화각을 조금 넓혀 주변부 흐름을 키웁니다. 카메라를 당기지 않으므로
   * 차량 크기는 유지되고 속도감만 올라갑니다.
   */
  const speedRatio = Math.min(1, Math.abs(state.speed) / 75);
  const targetFov = pose.fov + speedRatio * (pose.mounted ? 7 : 5);
  const fov = MathUtils.damp(camera.fov, targetFov, FOV_DAMPING, dt);
  if (Math.abs(fov - camera.fov) > 0.001) {
    camera.fov = fov;
    camera.updateProjectionMatrix();
  }

  camera.lookAt(lookAt);
}

const COLLISION_SHAKE_DURATION_SECONDS = 0.55;
const COLLISION_SHAKE_DECAY = 6.5;

/** 기존 추적 pose와 분리해 관리하는 충돌 흔들림 런타임입니다. */
export interface DrivingCameraShakeState {
  sequence: number;
  elapsedSeconds: number;
  strength: number;
  normalX: number;
  normalZ: number;
  /** 직전 프레임에 camera.position에 더한 값. 다음 base 갱신 전에 제거합니다. */
  positionOffset: Vector3;
  /** base lookAt을 변경하지 않고 최종 시선만 흔들기 위한 scratch입니다. */
  shakenLookAt: Vector3;
}

export function createDrivingCameraShakeState(): DrivingCameraShakeState {
  return {
    sequence: 0,
    elapsedSeconds: COLLISION_SHAKE_DURATION_SECONDS,
    strength: 0,
    normalX: 0,
    normalZ: 0,
    positionOffset: new Vector3(),
    shakenLookAt: new Vector3(),
  };
}

export function resetDrivingCameraShake(state: DrivingCameraShakeState): void {
  state.elapsedSeconds = COLLISION_SHAKE_DURATION_SECONDS;
  state.strength = 0;
  state.normalX = 0;
  state.normalZ = 0;
  state.positionOffset.set(0, 0, 0);
}

export function beginDrivingCameraShake(
  state: DrivingCameraShakeState,
  sequence: number,
  strength: number,
  normalX: number,
  normalZ: number
): void {
  const normalLength = Math.hypot(normalX, normalZ);
  state.sequence = sequence;
  state.elapsedSeconds = 0;
  state.strength = Math.min(1, Math.max(0, strength));
  state.normalX = normalLength > 1e-6 ? normalX / normalLength : 1;
  state.normalZ = normalLength > 1e-6 ? normalZ / normalLength : 0;
}

/** 직전 최종 pose의 shake만 빼서 camera.position을 순수 추적 상태로 되돌립니다. */
export function restoreDrivingCameraBase(
  camera: PerspectiveCamera,
  state: DrivingCameraShakeState
): void {
  camera.position.sub(state.positionOffset);
  state.positionOffset.set(0, 0, 0);
}

/**
 * 충돌 방향과 sequence로 결정되는 분석적 진동을 base pose에 마지막으로 합성합니다.
 * Math.random이나 프레임별 감쇠 곱셈을 쓰지 않아 같은 경과 시간에는 같은 offset입니다.
 */
export function applyDrivingCameraShake(
  camera: PerspectiveCamera,
  baseLookAt: Vector3,
  pose: DrivingCameraPose,
  dt: number,
  state: DrivingCameraShakeState
): void {
  if (state.strength <= 0 || state.elapsedSeconds >= COLLISION_SHAKE_DURATION_SECONDS) {
    return;
  }

  state.elapsedSeconds = Math.min(
    COLLISION_SHAKE_DURATION_SECONDS,
    state.elapsedSeconds + Math.min(0.05, Math.max(0, dt))
  );
  const age = state.elapsedSeconds;
  const lifetime = Math.max(0, 1 - age / COLLISION_SHAKE_DURATION_SECONDS);
  const envelope = state.strength * lifetime * Math.exp(-COLLISION_SHAKE_DECAY * age);
  if (envelope <= 0.0005) {
    state.strength = 0;
    return;
  }

  // 서로 다른 충돌이 같은 위상으로 보이지 않게 단조 sequence를 결정적 seed로 씁니다.
  const phase = ((state.sequence * 0.61803398875) % 1) * Math.PI * 2;
  const kick = Math.sin(age * 52 + phase);
  const jitter = Math.sin(age * 79 + phase * 1.73);
  const sideX = -state.normalZ;
  const sideZ = state.normalX;
  const positionGain = pose.mounted ? 0.075 : 0.18;
  const positionAmplitude = positionGain * envelope;

  state.positionOffset.set(
    (state.normalX * kick + sideX * jitter * 0.38) * positionAmplitude,
    jitter * positionAmplitude * 0.42,
    (state.normalZ * kick + sideZ * jitter * 0.38) * positionAmplitude
  );
  camera.position.add(state.positionOffset);

  const lookAmplitude = (pose.mounted ? 0.13 : 0.22) * envelope;
  state.shakenLookAt.copy(baseLookAt);
  state.shakenLookAt.x +=
    sideX * jitter * lookAmplitude + state.normalX * kick * lookAmplitude * 0.22;
  state.shakenLookAt.y += kick * lookAmplitude * 0.5;
  state.shakenLookAt.z +=
    sideZ * jitter * lookAmplitude + state.normalZ * kick * lookAmplitude * 0.22;
  camera.lookAt(state.shakenLookAt);
}
